import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { CenteredShell, Eyebrow, Headline } from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { setActiveBrandId } from '../lib/active-brand.js';
import { signOut } from '../lib/auth-client.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import {
  acceptInvitation,
  fetchInvitationPreview,
  InvitationApiError,
  type InvitationPreview,
} from '../lib/invitations-api.js';
import { meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// the team access design doc (CommitMind) T2 — invitation accept page.
//
// Resolution sequence:
//   1. fetch preview (no auth required) → render org + role + brands
//   2. fetch /api/me to know session state
//   3. branch:
//      - no session → prompt to sign in / sign up; carry token through
//      - signed in, email mismatch → say so + offer sign-out
//      - signed in, email matches → big "accept" button → POST accept

const searchSchema = z.object({
  token: z.string().min(20).max(120).optional(),
  email: z.string().optional(),
});

export const Route = createFileRoute('/accept')({
  validateSearch: searchSchema,
  component: AcceptInvitePage,
});

type BrandRole = 'admin' | 'manager' | 'editor' | 'viewer' | 'designer';

/** OUR enum, rendered to a founder as prose — so it gets a translated
 *  label. (A THIRD-PARTY enum would not: "Meta CAPI" stays "Meta CAPI".) */
const ROLE_LABELS: Record<BrandRole, MessageDescriptor> = {
  admin: msg`admin`,
  manager: msg`manager`,
  editor: msg`editor`,
  viewer: msg`viewer`,
  designer: msg`designer`,
};

const roleLabel = (role: BrandRole): string => i18n._(ROLE_LABELS[role]);

function describeRole(workspaceRole: 'admin' | 'member', preview: InvitationPreview): string {
  if (workspaceRole === 'admin') return i18n._(msg`admin — every brand, every approval.`);
  if (preview.brandGrants.length === 0) return i18n._(msg`on the team.`);
  const parts = preview.brandGrants.map((g) =>
    i18n._(msg`${roleLabel(g.role)} on ${g.name.toLowerCase()}`),
  );
  // Intl.ListFormat rather than the hand-rolled ", " + " and " this used
  // to do: Spanish joins with "y" and has no Oxford comma, and the join
  // rules are the locale's to decide, not ours.
  return `${new Intl.ListFormat(i18n.locale, { type: 'conjunction' }).format(parts)}.`;
}

// Per-role short-form list of what the invitee can do. Two-line max so
// the card stays compact under the headline + serif quote. Lowercased
// per Mag's voice rules.
const BRAND_ROLE_CAPS: Record<'manager' | 'editor' | 'viewer' | 'designer', MessageDescriptor[]> = {
  manager: [
    msg`approve mag on every move`,
    msg`edit the brief + ad accounts`,
    msg`full access to the brand`,
  ],
  editor: [msg`approve mag's proposals`, msg`reply in chat with mag`, msg`see analytics + recaps`],
  viewer: [msg`see the feed, recaps, analytics`, msg`no approvals — read-only`],
  designer: [
    msg`upload assets for slots routed to you`,
    msg`see the brand brief + brand assets`,
    msg`no approvals, no campaign access`,
  ],
};

const ADMIN_CAPS = [
  msg`every brand in the workspace`,
  msg`invite + manage teammates`,
  msg`approve mag on every move`,
];

function AcceptInvitePage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<InvitationPreview | null | undefined>(undefined);
  const [meEmail, setMeEmail] = useState<string | null | undefined>(undefined);
  const [accepting, setAccepting] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // 1. Preview + me in parallel on mount.
  useEffect(() => {
    let cancelled = false;
    const token = search.token;
    if (!token) {
      setPreview(null);
      setPreviewError('no_token');
      return;
    }
    (async () => {
      try {
        const [previewResult, meResult] = await Promise.all([
          fetchInvitationPreview(token),
          queryClient.ensureQueryData(meQuery()),
        ]);
        if (cancelled) return;
        setPreview(previewResult);
        setMeEmail(meResult?.email ?? null);
      } catch (err) {
        if (cancelled) return;
        const detail = founderErrorCopy(err, 'load failed');
        setPreviewError(detail);
        setPreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.token, queryClient]);

  if (preview === undefined || meEmail === undefined) {
    return (
      <CenteredShell>
        <Mag size={96} />
        <Eyebrow>
          <Trans>invite</Trans>
        </Eyebrow>
        <Headline>
          <Trans>one sec.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/70">
          <Trans>“reading the invite…”</Trans>
        </p>
      </CenteredShell>
    );
  }

  if (!preview) {
    return (
      <CenteredShell>
        <Mag size={96} mood="confused" />
        <Eyebrow>
          <Trans>invite</Trans>
        </Eyebrow>
        <Headline>
          <Trans>we don't have that one.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/70">
          <Trans>
            “the link's either wrong or older than a week. ask whoever invited you for a fresh one.”
          </Trans>
        </p>
        {previewError === 'no_token' ? null : (
          <p className="text-[13px] font-mono text-[var(--ink)]/50">{previewError}</p>
        )}
        <Link to="/login" className="link-plum">
          <Trans>back to sign in</Trans>
        </Link>
      </CenteredShell>
    );
  }

  if (preview.status === 'expired') {
    return (
      <CenteredShell>
        <Mag size={96} mood="confused" />
        <Eyebrow>
          <Trans>invite expired</Trans>
        </Eyebrow>
        <Headline>
          <Trans>this one timed out.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/70">
          <Trans>
            “ask {preview.inviterName.split(' ')[0]?.toLowerCase()} to send a new one — same email
            works.”
          </Trans>
        </p>
      </CenteredShell>
    );
  }
  if (preview.status === 'revoked') {
    return (
      <CenteredShell>
        <Mag size={96} mood="confused" />
        <Eyebrow>
          <Trans>invite revoked</Trans>
        </Eyebrow>
        <Headline>
          <Trans>this one's off the table.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/70">
          <Trans>
            “{preview.inviterName.split(' ')[0]?.toLowerCase()} pulled it back. if that's a
            surprise, ping them directly.”
          </Trans>
        </p>
      </CenteredShell>
    );
  }
  if (preview.status === 'accepted') {
    return (
      <CenteredShell>
        <Mag size={96} />
        <Eyebrow>
          <Trans>already in</Trans>
        </Eyebrow>
        <Headline>
          <Trans>this invite was already used.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/70">
          <Trans>“you're already on the workspace. head to /home.”</Trans>
        </p>
        <Link to="/home" className="link-plum">
          <Trans>go to /home →</Trans>
        </Link>
      </CenteredShell>
    );
  }

  // status === 'pending' below
  const inviterFirstName = preview.inviterName.split(' ')[0]?.toLowerCase() ?? i18n._(msg`someone`);
  const summary = describeRole(preview.workspaceRole, preview);

  // No session — most invitees don't have a magig account yet, so the
  // primary action is create-account; sign-in is the secondary path.
  // Token stays in the URL so they land back here after auth.
  if (meEmail === null) {
    const continueUrl = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    return (
      <CenteredShell>
        <Mag size={120} />
        <Eyebrow>
          <Trans>invite</Trans>
        </Eyebrow>
        <Headline>
          <Trans>{inviterFirstName} wants you in.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/80">
          <Trans>
            “i'm mag. i run the ads for {preview.organizationName.toLowerCase()}. set up your
            account and i'll add you to the team.”
          </Trans>
        </p>
        <p className="text-[14px] text-[var(--ink)]/70">
          <Trans>you'll be {summary}</Trans>
        </p>
        <p className="text-[13px] font-mono text-[var(--ink)]/55">
          <Trans>invited as {preview.invitedEmail}</Trans>
        </p>
        <div className="mt-4 flex w-full max-w-[320px] flex-col gap-2 self-center">
          <Link
            to="/signup"
            search={{ next: continueUrl, email: preview.invitedEmail }}
            className="btn btn-primary w-full"
          >
            <Trans>create account + accept →</Trans>
          </Link>
          <Link
            to="/login"
            search={{ next: continueUrl, email: preview.invitedEmail }}
            className="link-quiet text-center"
          >
            <Trans>already have one? sign in →</Trans>
          </Link>
        </div>
      </CenteredShell>
    );
  }

  // Signed in, but as the wrong identity for this invite.
  if (meEmail.toLowerCase() !== preview.invitedEmail.toLowerCase()) {
    return (
      <CenteredShell>
        <Mag size={96} mood="confused" />
        <Eyebrow>
          <Trans>email mismatch</Trans>
        </Eyebrow>
        <Headline>
          <Trans>wrong inbox.</Trans>
        </Headline>
        <p className="mag-voice text-xl text-[var(--ink)]/80">
          <Trans>
            “this invite is for <span className="font-mono not-italic">{preview.invitedEmail}</span>{' '}
            — sign out and back in as that account.”
          </Trans>
        </p>
        <p className="text-[13px] font-mono text-[var(--ink)]/55">
          <Trans>you're currently signed in as {meEmail}</Trans>
        </p>
        <div className="mt-4 flex w-full max-w-[320px] flex-col gap-2 self-center">
          <button
            type="button"
            className="btn btn-primary w-full"
            onClick={async () => {
              await signOut();
              await router.invalidate();
              navigate({
                to: '/login',
                search: {
                  next: encodeURIComponent(`${window.location.pathname}${window.location.search}`),
                },
              });
            }}
          >
            <Trans>sign out →</Trans>
          </button>
        </div>
      </CenteredShell>
    );
  }

  // Match — show the big accept button.
  const handleAccept = async () => {
    if (!search.token) return;
    setAccepting(true);
    try {
      const result = await acceptInvitation(search.token);
      // Brand context cache must be rebuilt: brand list, brand brain
      // for the newly-granted brands, etc. The wholesale clear() is
      // the right tool here because every per-brand key needs to
      // refetch against the new access set.
      queryClient.clear();
      // Set the first granted brand as active so the next route load
      // lands somewhere coherent. Admins get the org's first brand
      // implicitly via the next /api/brands fetch (no brandIds in
      // result for them).
      const firstBrand = result.brandIds[0] ?? null;
      if (firstBrand) setActiveBrandId(firstBrand);
      await refreshQueries(queryClient, ['me']);
      await router.invalidate();
      magToast.success(
        i18n._(msg`you're in.`),
        i18n._(msg`welcome to ${preview.organizationName.toLowerCase()}.`),
      );
      navigate({ to: '/home' });
    } catch (err) {
      const detail =
        err instanceof InvitationApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : i18n._(msg`try again in a sec.`);
      magToast.error(i18n._(msg`couldn't accept.`), detail);
      setAccepting(false);
    }
  };

  return (
    <CenteredShell>
      <Mag size={120} />
      <Eyebrow>
        <Trans>invite</Trans>
      </Eyebrow>
      <Headline>
        <Trans>welcome to {preview.organizationName.toLowerCase()}.</Trans>
      </Headline>
      <p className="mag-voice text-xl text-[var(--ink)]/80">
        <Trans>
          “{inviterFirstName} added you to the team. tap accept and i'll show you around.”
        </Trans>
      </p>
      <InviteSummaryCard preview={preview} />
      <div className="mt-4 flex w-full max-w-[320px] flex-col gap-2 self-center">
        <button
          type="button"
          className="btn btn-primary w-full"
          onClick={handleAccept}
          disabled={accepting}
        >
          {accepting ? <Trans>accepting…</Trans> : <Trans>accept the invite →</Trans>}
        </button>
      </div>
    </CenteredShell>
  );
}

// Structured replacement for the old yellow ProTip. Shows the role
// scope as a tagged chip list (role × brand) followed by a compact
// capabilities preview, so an invitee at first sight knows exactly
// what they'll be able to do once they accept.
function InviteSummaryCard({ preview }: { preview: InvitationPreview }) {
  const isAdmin = preview.workspaceRole === 'admin';
  // Build a list of {role, scope} rows + the dedup'd capabilities for
  // the bullets below. Members carry their per-brand role; admin gets
  // a single "every brand" row.
  const rows: Array<{
    role: 'admin' | 'manager' | 'editor' | 'viewer' | 'designer';
    scope: string;
  }> = isAdmin
    ? [{ role: 'admin', scope: i18n._(msg`every brand`) }]
    : preview.brandGrants.map((g) => ({ role: g.role, scope: g.name.toLowerCase() }));

  const caps = isAdmin
    ? ADMIN_CAPS
    : // Union of every brand-role's capabilities so a multi-grant
      // invitee sees the strongest line for what they can do.
      Array.from(new Set(preview.brandGrants.flatMap((g) => BRAND_ROLE_CAPS[g.role] ?? []))).slice(
        0,
        3,
      );

  return (
    <div className="mt-2 w-full max-w-[420px] rounded-xl border border-[var(--line-strong)] bg-white p-4 text-left shadow-[3px_3px_0_var(--line-strong)]">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        <Trans>your role</Trans>
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={`${row.role}-${row.scope}`} className="flex items-center gap-2.5">
            <RoleChip role={row.role} />
            <span className="text-[13.5px] text-[var(--ink)]/80">
              <Trans>
                on <span className="font-semibold text-[var(--ink)]">{row.scope}</span>
              </Trans>
            </span>
          </li>
        ))}
      </ul>
      {caps.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 border-t border-[var(--line)] pt-3">
          {caps.map((c) => (
            <li
              key={c.id ?? String(c.message)}
              className="flex items-start gap-2 text-[12.5px] text-[var(--ink)]/70"
            >
              <span
                aria-hidden
                className="mt-[7px] inline-block h-1 w-1 shrink-0 rounded-full bg-[var(--ink)]/35"
              />
              <span>{i18n._(c)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RoleChip({ role }: { role: BrandRole }) {
  // Color-code the chip so the role is scannable at a glance. Admin
  // and manager (the privileged tiers) lean spell yellow; editor sits
  // on a quieter plum; viewer is neutral so it reads "read-only" at
  // first sight. Designer borrows the sage tone — visually distinct
  // from editor/viewer so it reads as "scoped, not a step on the
  // ladder."
  const palette: Record<typeof role, { bg: string; fg: string; border: string }> = {
    admin: { bg: 'var(--spell)', fg: 'var(--ink)', border: 'var(--ink)' },
    manager: { bg: 'var(--spell)', fg: 'var(--ink)', border: 'var(--ink)' },
    editor: { bg: 'var(--plum)', fg: 'var(--cream)', border: 'var(--plum)' },
    viewer: { bg: 'var(--paper)', fg: 'var(--ink)', border: 'var(--line-strong)' },
    designer: { bg: 'var(--sage)', fg: 'var(--cream)', border: 'var(--sage)' },
  };
  const c = palette[role];
  return (
    <span
      className="inline-flex items-center rounded-full border-[1.2px] px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em]"
      style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.border }}
    >
      {roleLabel(role)}
    </span>
  );
}
